// ── Notification Center (NOTIF.4 · /notifications · redesign P1/P2/P4 + three-pane rail) ─────────
// Three-pane (Design updated card 1/2): a 200px LEFT NAV RAIL (Inbox/Archived folders · Read state ·
// Categories · Priorities with colour dots — no counts, card 17) | the list | the reading pane. The
// reading pane is the 3-ZONE SCAFFOLD (P1) — header (title + status tag + actor line, COLLAB-NOTIF S1)
// → body → reserved collab region (Zone 3, still parked for the notes feature).
//
// Responsive: three-pane ≥1280px; the rail collapses to a "Filters" drawer <1280px; the detail
// collapses to a Sheet <1024px. Backend: existing /api/v1/inbox + the P4-additive state=archived /
// restore. Optimistic mark-read/archive/restore + deep-link resolver. "Dismiss" is surfaced as "Archive".

import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bell, FileText, Shield, AlertTriangle, Sparkles, Settings, Search, SlidersHorizontal,
  Archive, ArchiveRestore, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, Inbox, X, MessageSquare,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  useInboxList,
  useUnreadCount,
  useMarkNotificationRead,
  useMarkNotificationUnread,
  useDismissNotification,
  useMarkReadBulk,
  useMarkUnreadBulk,
  useDismissBulk,
  useRestoreNotification,
  useRestoreBulk,
  useResourceStatuses,
  type Notification,
} from '@/api/hooks/useNotifications';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useMediaQuery } from '@/lib/hooks/useMediaQuery';
import { resolveNotificationRoute } from '@/lib/notificationRoute';
import { ResourceNotes } from '@/components/notes/ResourceNotes';
import type { ResourceNoteType } from '@/api/hooks/useResourceNotes';
import { cn } from '@/lib/utils';

// COLLAB-NOTIF S2 — notification resource_types that host resource notes.
const NOTE_RESOURCE_TYPES = ['risk_acceptance', 'org_risk_acceptance', 'assessment'];
function noteLabelFor(resourceType: string | null): string {
  return resourceType === 'assessment' ? 'assessment' : 'risk response';
}

const PER_PAGE = 20;

const CATEGORY_ICON: Record<string, typeof AlertTriangle> = {
  assessment: FileText, compliance: Shield, tenant_workflow: AlertTriangle,
  template_version: FileText, product_update: Sparkles, system_admin: Settings,
};
const CATEGORY_LABEL: Record<string, string> = {
  tenant_workflow: 'Workflow', assessment: 'Assessment', compliance: 'Compliance',
  template_version: 'Template', product_update: 'Product', system_admin: 'System',
};
const CATEGORIES = Object.keys(CATEGORY_LABEL);
const PRIORITIES = ['critical', 'high', 'medium', 'low'] as const;
// Priority colours — the app's canonical severity tokens (VendorBiHeader precedent). Rail = solid dots;
// row/pane = a tinted pill (Design: red/amber pills, not grey text).
const PRIORITY_COLOR: Record<string, string> = {
  critical: 'bg-severity-critical', high: 'bg-severity-high',
  medium: 'bg-severity-medium', low: 'bg-severity-low',
};
const PRIORITY_PILL: Record<string, string> = {
  critical: 'bg-severity-critical/15 text-severity-critical',
  high: 'bg-severity-high/15 text-severity-high',
  medium: 'bg-severity-medium/15 text-severity-medium',
  low: 'bg-severity-low/15 text-severity-low',
};

function iconFor(n: Notification) { return CATEGORY_ICON[n.category] ?? Bell; }
function catLabel(c: string) { return CATEGORY_LABEL[c] ?? c; }
function cap(s: string) { return s[0].toUpperCase() + s.slice(1); }
// Subject-leads (row polish): split the title on the FIRST colon → the distinguishing subject leads;
// the shared prefix drops to a muted, clipped second line. No colon → the whole title is the subject.
function splitSubject(title: string): { prefix: string | null; subject: string } {
  const i = title.indexOf(':');
  if (i < 0) return { prefix: null, subject: title };
  return { prefix: title.slice(0, i).trim(), subject: title.slice(i + 1).trim() || title };
}

// ── Priority pill ────────────────────────────────────────────────────────────
function PriorityPill({ priority }: { priority: string }) {
  return (
    <span className={cn('inline-flex items-center rounded px-1.5 py-0 text-[10px] font-medium capitalize', PRIORITY_PILL[priority] ?? 'bg-bg-muted text-text-secondary')}>
      {priority}
    </span>
  );
}

// ── Rail primitives ──────────────────────────────────────────────────────────
function RailRow({
  label, active, onClick, Icon, dotColor,
}: {
  label: string; active: boolean; onClick: () => void;
  Icon?: typeof AlertTriangle; dotColor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
        active ? 'bg-bg-muted font-semibold text-text-primary' : 'text-text-secondary hover:bg-bg-muted/60',
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {dotColor !== undefined && <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', dotColor || 'bg-text-faint')} />}
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );
}
function RailSection({
  title, collapsible, open, onToggle, children,
}: {
  title: string; collapsible?: boolean; open?: boolean; onToggle?: () => void; children: ReactNode;
}) {
  const expanded = collapsible ? !!open : true;
  return (
    <div className="flex flex-col gap-0.5">
      {collapsible ? (
        <button type="button" onClick={onToggle} className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-text-secondary">
          <ChevronDown className={cn('h-3 w-3 transition-transform', expanded ? '' : '-rotate-90')} />
          {title}
        </button>
      ) : (
        <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-text-secondary">{title}</span>
      )}
      {expanded && children}
    </div>
  );
}

// ── The left nav rail (shown inline ≥1280px; in a drawer below) ───────────────
function NavRail({
  archived, state, category, priority, catOpen, priOpen, setCatOpen, setPriOpen,
  onFolder, onState, onCategory, onPriority,
}: {
  archived: boolean; state: string; category?: string; priority?: string;
  catOpen: boolean; priOpen: boolean; setCatOpen: (v: boolean) => void; setPriOpen: (v: boolean) => void;
  onFolder: (f: 'inbox' | 'archived') => void; onState: (s: string | undefined) => void;
  onCategory: (c: string | undefined) => void; onPriority: (p: string | undefined) => void;
}) {
  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <RailRow Icon={Inbox} label="Inbox" active={!archived} onClick={() => onFolder('inbox')} />
        <RailRow Icon={Archive} label="Archived" active={archived} onClick={() => onFolder('archived')} />
      </div>

      {/* Read state — a rail section (Design updated card 2); applies in the Inbox folder only. */}
      {!archived && (
        <RailSection title="Read state">
          <RailRow label="All" active={state !== 'unread'} onClick={() => onState(undefined)} />
          <RailRow label="Unread" active={state === 'unread'} onClick={() => onState('unread')} />
        </RailSection>
      )}

      <RailSection title="Categories" collapsible open={catOpen} onToggle={() => setCatOpen(!catOpen)}>
        <RailRow label="All categories" active={!category} onClick={() => onCategory(undefined)} />
        {CATEGORIES.map((c) => (
          <RailRow key={c} Icon={CATEGORY_ICON[c]} label={CATEGORY_LABEL[c]} active={category === c} onClick={() => onCategory(c)} />
        ))}
      </RailSection>

      <RailSection title="Priorities" collapsible open={priOpen} onToggle={() => setPriOpen(!priOpen)}>
        <RailRow label="All priorities" dotColor="" active={!priority} onClick={() => onPriority(undefined)} />
        {PRIORITIES.map((p) => (
          <RailRow key={p} dotColor={PRIORITY_COLOR[p]} label={cap(p)} active={priority === p} onClick={() => onPriority(p)} />
        ))}
      </RailSection>

      {/* Guard (card 17) — no per-facet counts in the rail; visible, not tooltip-only. */}
      <p className="mt-auto px-2.5 text-[10.5px] leading-snug text-text-faint">
        No counts here: the list is capped at 100 rows, so any per-facet tally would describe the fetched page, not the inbox. The header counts are whole-inbox.
      </p>
    </div>
  );
}

// ── The 3-zone reading pane (shared by the desktop pane + the mobile Sheet) ───
function NotificationDetail({
  n, archived, onArchive, onRestore, onMarkUnread, onOpen,
}: {
  n: Notification;
  archived: boolean;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onOpen: (route: string) => void;
}) {
  const route = resolveNotificationRoute(n.resource_type, n.resource_id);
  const { prefix, subject } = splitSubject(n.title);
  // COLLAB-NOTIF S1 — server-authoritative status + actor + attention overlay for
  // the open notification. Honest-empty: absent (non-workflow / not visible) → no tag.
  const { data: statuses } = useResourceStatuses([n.id]);
  const sv = statuses?.[n.id];
  return (
    <div className="flex h-full flex-col">
      {/* ── ZONE 1 · HEADER — subject leads; shared prefix muted; badge row (status tag + actor); actions ── */}
      <div className="border-b border-border-subtle px-5 py-4">
        <h2 className="text-base font-semibold leading-snug text-text-primary">{subject}</h2>
        {prefix && <div className="mt-0.5 truncate text-xs text-text-secondary">{prefix}</div>}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-text-secondary">
          <Badge variant="muted" className="px-1.5 py-0 text-[10px]">{catLabel(n.category)}</Badge>
          <PriorityPill priority={n.priority} />
          <span>· {formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}</span>
          {/* COLLAB-NOTIF S1 status slot — base tag + attention adornment (overlay, never masks base). */}
          {sv && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex items-center rounded border border-border-subtle bg-bg-surface-2 px-1.5 py-0 text-[10px] font-medium text-text-primary">
                {sv.status}
              </span>
              {sv.attention && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-medium text-severity-high"
                  title="This resource needs attention"
                >
                  <AlertTriangle className="h-3 w-3" /> Requires attention
                </span>
              )}
            </span>
          )}
        </div>
        {/* Actor line — 'Actioned by …' / 'Reviewed by …'; honest-empty when no actor. */}
        {sv?.actorLine && <div className="mt-1 text-xs text-text-secondary">{sv.actorLine}</div>}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={!route}
            title={route ? undefined : 'No linked resource'}
            onClick={() => route && onOpen(route)}
          >
            <ExternalLink className="h-4 w-4" /> Open {route ? catLabel(n.category) : 'resource'}
          </Button>
          {archived ? (
            <Button size="sm" variant="ghost" onClick={() => onRestore(n.id)}>
              <ArchiveRestore className="h-4 w-4" /> Restore to inbox
            </Button>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => onArchive(n.id)}>
                <Archive className="h-4 w-4" /> Archive
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onMarkUnread(n.id)}>
                Mark unread
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── ZONE 2 · BODY ── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {n.body
          ? <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-primary">{n.body}</p>
          : <p className="text-sm text-text-secondary">No additional detail for this notification.</p>}
      </div>

      {/* ── ZONE 3 · COLLAB REGION — resource notes (COLLAB-NOTIF S2). No linked resource → no section. ── */}
      {n.resource_id && NOTE_RESOURCE_TYPES.includes(n.resource_type ?? '') && (
        <div className="border-t border-border-subtle px-5 py-4">
          <ResourceNotes
            resourceType={n.resource_type as ResourceNoteType}
            resourceId={n.resource_id}
            host="pane"
            resourceLabel={noteLabelFor(n.resource_type)}
          />
        </div>
      )}
    </div>
  );
}

export function NotificationCenterPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isXl = useMediaQuery('(min-width: 1280px)');

  const page = Math.max(parseInt(params.get('page') ?? '1', 10) || 1, 1);
  const category = params.get('category') ?? undefined;
  const priority = params.get('priority') ?? undefined;
  const state = (params.get('state') as 'all' | 'unread' | 'archived') ?? 'all';
  const archived = state === 'archived';
  const rawQ = params.get('q') ?? '';
  const q = useDebounce(rawQ, 300);
  const hasFilter = !!(category || priority || (!archived && state === 'unread') || q);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reading, setReading] = useState<Notification | null>(null);
  const [railOpen, setRailOpen] = useState(false);   // the <1280px filter drawer
  const [catOpen, setCatOpen] = useState(true);       // collapsible rail sections (card 17)
  const [priOpen, setPriOpen] = useState(true);

  const { data, isLoading } = useInboxList({
    page, perPage: PER_PAGE, category, priority, state, q: q || undefined,
  });
  const items = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;
  // Header stat (card 2) — both figures WHOLE-INBOX; the filtered view count lives in the list footer.
  const { data: unreadCount } = useUnreadCount();
  const unread = unreadCount ?? 0;
  const { data: inboxAll } = useInboxList({ page: 1, perPage: 1 });
  const inboxTotal = inboxAll?.meta.total ?? Math.max(unread, items.length);

  const markRead = useMarkNotificationRead();
  const markUnread = useMarkNotificationUnread();
  const archive = useDismissNotification();
  const markReadBulk = useMarkReadBulk();
  const markUnreadBulk = useMarkUnreadBulk();
  const archiveBulk = useDismissBulk();
  const restore = useRestoreNotification();
  const restoreBulk = useRestoreBulk();

  function setParam(key: string, value: string | undefined, resetPage = true) {
    const next = new URLSearchParams(params);
    if (value === undefined || value === '') next.delete(key); else next.set(key, value);
    if (resetPage && key !== 'page') next.delete('page');
    setParams(next, { replace: true });
    setSelectedIds(new Set());
    setReading(null);
  }
  function switchFolder(folder: 'inbox' | 'archived') {
    // Card 12/17: a folder switch clears selection, the open pane, AND the filters.
    const next = new URLSearchParams();
    if (folder === 'archived') next.set('state', 'archived');
    setParams(next, { replace: true });
    setSelectedIds(new Set());
    setReading(null);
    setRailOpen(false);
  }
  function clearFilters() {
    const next = new URLSearchParams();
    if (archived) next.set('state', 'archived'); // stay in the folder; clear its filters
    setParams(next, { replace: true });
    setSelectedIds(new Set());
    setReading(null);
  }

  const pageIds = useMemo(() => items.map((n) => n.id), [items]);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  function toggleAll() { setSelectedIds(allSelected ? new Set() : new Set(pageIds)); }
  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  const selection = [...selectedIds];

  function openRow(n: Notification) {
    setReading(n);
    if (!n.was_read) markRead.mutate(n.id);
  }

  // Active-filter chips (mirror the rail selection) for the filter area.
  const chips: { label: string; clear: () => void }[] = [];
  if (category) chips.push({ label: `Category: ${catLabel(category)}`, clear: () => setParam('category', undefined) });
  if (priority) chips.push({ label: `Priority: ${cap(priority)}`, clear: () => setParam('priority', undefined) });
  if (!archived && state === 'unread') chips.push({ label: 'Unread', clear: () => setParam('state', undefined) });

  const railProps = {
    archived, state, category, priority, catOpen, priOpen, setCatOpen, setPriOpen,
    onFolder: switchFolder,
    onState: (s: string | undefined) => setParam('state', s),
    onCategory: (c: string | undefined) => { setParam('category', c); setRailOpen(false); },
    onPriority: (p: string | undefined) => { setParam('priority', p); setRailOpen(false); },
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      {/* COLLAB-NOTIF polish #2 — header + search/filter toolbar on a #111E30 surface bar (bg-bg-surface),
          anchored like the rail; the search / filter-chip / Clear-all behavior is unchanged, only the
          surface wrapper is added. */}
      <div className="mb-4 rounded-lg border border-border-subtle bg-bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <Bell className="h-5 w-5 text-text-secondary" />
        <h1 className="text-lg font-semibold text-text-primary">Notifications</h1>
        {/* Header stat — compact inline two-figure display (both WHOLE-INBOX server scalars; the honest-BI
            guard lives in the rail footnote). Matches the mock's summary tile. */}
        <div className="ml-auto flex items-baseline gap-4">
          <span className="flex items-baseline gap-1.5">
            <span className="text-[17px] font-bold tabular-nums text-text-primary">{inboxTotal}</span>
            <span className="text-[11.5px] text-text-secondary">total</span>
          </span>
          <span className="flex items-baseline gap-1.5">
            <span className="text-[17px] font-bold tabular-nums text-text-primary">{unread}</span>
            <span className="text-[11.5px] text-text-secondary">unread</span>
          </span>
        </div>
      </div>

      {/* Search + filters (COLLAB-NOTIF S2): a grouped search control, and a dedicated
          filter-chip area mirroring the rail selection with Clear all. Filters button <1280px. */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {!isXl && (
          <Button size="sm" variant="outline" onClick={() => setRailOpen(true)}>
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </Button>
        )}
        {/* grouped: search field + Search button as one control (bg drops to bg-base inside the surface bar) */}
        <div className="flex min-w-[15rem] max-w-[480px] flex-1 items-center overflow-hidden rounded-md border border-border-subtle bg-bg-base">
          <Search className="ml-2.5 h-4 w-4 shrink-0 text-text-secondary" />
          <Input
            className="h-8 flex-1 border-0 bg-transparent px-2 shadow-none focus-visible:ring-0"
            placeholder="Search titles…"
            value={rawQ}
            onChange={(e) => setParam('q', e.target.value || undefined)}
            onKeyDown={(e) => { if (e.key === 'Enter') setParam('q', rawQ || undefined); }}
          />
          <button
            type="button"
            onClick={() => setParam('q', rawQ || undefined)}
            className="h-8 border-l border-border-subtle px-3 text-[12px] font-semibold text-text-secondary hover:bg-bg-surface-2 hover:text-text-primary"
          >
            Search
          </button>
        </div>
        {/* filter-chip area — only when a filter is active */}
        {hasFilter && (
          <div className="flex flex-wrap items-center gap-2">
            {chips.length > 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wide text-text-faint">Filters</span>
            )}
            {chips.map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1 rounded bg-bg-muted px-2 py-0.5 text-[11px] text-text-primary">
                {c.label}
                <button type="button" aria-label={`Remove ${c.label} filter`} onClick={c.clear} className="text-text-secondary hover:text-text-primary">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <button type="button" onClick={clearFilters} className="px-1 text-[11px] font-semibold text-accent-cyan hover:underline">
              Clear all
            </button>
          </div>
        )}
      </div>
      </div>{/* /surface bar */}

      {/* Bulk action bar */}
      {selection.length > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm">
          <span className="text-text-secondary">{selection.length} selected</span>
          {archived ? (
            <Button size="sm" variant="ghost" onClick={() => restoreBulk.mutate(selection, { onSuccess: () => setSelectedIds(new Set()) })}>
              <ArchiveRestore className="h-4 w-4" /> Restore to inbox
            </Button>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => markReadBulk.mutate(selection, { onSuccess: () => setSelectedIds(new Set()) })}>
                Mark as read
              </Button>
              <Button size="sm" variant="ghost" onClick={() => markUnreadBulk.mutate(selection, { onSuccess: () => setSelectedIds(new Set()) })}>
                Mark as unread
              </Button>
              <Button size="sm" variant="ghost" onClick={() => archiveBulk.mutate(selection, { onSuccess: () => setSelectedIds(new Set()) })}>
                <Archive className="h-4 w-4" /> Archive
              </Button>
            </>
          )}
        </div>
      )}

      {/* ── Grid: base 1-col (list; detail→Sheet, rail→drawer) · lg 2-col [list|detail] · xl 3-col [rail|list|detail] ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(320px,400px)_1fr] xl:grid-cols-[200px_minmax(320px,1fr)_minmax(360px,1.02fr)]">
        {/* RAIL — inline ≥1280px (CSS-hidden below; the Filters drawer serves <1280px). */}
        <div className="hidden xl:block">
          <NavRail {...railProps} />
        </div>

        {/* LIST rail (list + footer view-count/paging) */}
        <div className="flex flex-col gap-3">
          <Card>
            <CardContent className="p-0">
              {items.length > 0 && (
                <div className="flex items-center gap-3 border-b border-border-subtle px-4 py-2">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                  <span className="text-xs text-text-secondary">Select all on page</span>
                </div>
              )}

              {isLoading ? (
                <div className="space-y-2 p-4">
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : items.length === 0 ? (
                <div className="py-12">
                  {hasFilter ? (
                    <EmptyState icon={Search} title="No matching notifications" description="No notifications match the current filters." action={{ label: 'Clear filters', onClick: clearFilters }} />
                  ) : archived ? (
                    <EmptyState icon={Archive} title="No archived notifications" description="Notifications you archive are kept here, not deleted — nothing archived yet." />
                  ) : (
                    <EmptyState icon={Inbox} title="No notifications" description="You're all caught up." />
                  )}
                </div>
              ) : (
                items.map((n) => {
                  const Icon = iconFor(n);
                  const isOpen = reading?.id === n.id;
                  const { prefix, subject } = splitSubject(n.title);
                  return (
                    <div
                      key={n.id}
                      className={cn(
                        'flex items-start gap-3 border-b border-border-subtle px-4 py-3 last:border-0',
                        // Selected/open row (mock): a lighter surface + a 3px blue left edge. The edge is a
                        // `before:` pseudo-bar built from standard utilities (bg-accent-cyan) — no arbitrary
                        // theme() in a shadow (which silently failed to generate), and no layout shift.
                        isOpen
                          ? "relative bg-bg-surface-2 before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-accent-cyan before:content-['']"
                          : 'hover:bg-bg-muted',
                      )}
                    >
                      <Checkbox checked={selectedIds.has(n.id)} onCheckedChange={() => toggleOne(n.id)} />
                      <button type="button" className="flex min-w-0 flex-1 items-start gap-2.5 text-left" onClick={() => openRow(n)}>
                        <span aria-hidden className={cn('mt-[7px] h-2 w-2 shrink-0 rounded-full', n.was_read ? 'bg-transparent' : 'bg-accent-cyan')} />
                        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', n.was_read ? 'text-text-secondary' : 'text-text-primary')} />
                        <span className="flex min-w-0 flex-col gap-0.5">
                          {/* Subject-leads: distinguishing subject at full contrast; shared prefix muted + clipped below. */}
                          <span className={cn('truncate text-sm leading-snug', n.was_read ? 'font-normal text-text-secondary' : 'font-semibold text-text-primary')}>
                            {subject}
                          </span>
                          {prefix && <span className="truncate text-[11px] text-text-faint">{prefix}</span>}
                          <span className="flex items-center gap-2 text-[11px] text-text-secondary">
                            <Badge variant="muted" className="px-1.5 py-0 text-[10px]">{catLabel(n.category)}</Badge>
                            <PriorityPill priority={n.priority} />
                            <span>· {formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}</span>
                            {/* S2 card 8 — notes count: speech-bubble + number, neutral; zero renders nothing */}
                            {(n.notes_count ?? 0) > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10.5px] text-text-faint" title={`${n.notes_count} note${n.notes_count === 1 ? '' : 's'}`}>
                                <MessageSquare className="h-3 w-3" />{n.notes_count}
                              </span>
                            )}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        aria-label={archived ? 'Restore to inbox' : 'Archive notification'}
                        title={archived ? 'Restore to inbox' : 'Archive'}
                        className="shrink-0 rounded p-1 text-text-secondary hover:bg-bg-surface hover:text-text-primary"
                        onClick={() => { (archived ? restore : archive).mutate(n.id); if (reading?.id === n.id) setReading(null); }}
                      >
                        {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </button>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {meta && items.length > 0 && (
            <div className="flex items-center justify-between gap-3 text-xs text-text-secondary">
              <span>
                {meta.totalPages > 1 ? `Page ${meta.page} of ${meta.totalPages} · ` : ''}
                {items.length} of {meta.total} in view
              </span>
              {meta.totalPages > 1 && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setParam('page', String(page - 1), false)}>
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= meta.totalPages} onClick={() => setParam('page', String(page + 1), false)}>
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* DETAIL pane — desktop ≥1024px (auto-placed: lg col 2, xl col 3; the hidden rail drops out of the lg grid) */}
        <Card className="hidden lg:block lg:sticky lg:top-6 lg:h-[calc(100vh-11rem)] lg:overflow-hidden">
          <CardContent className="h-full p-0">
            {reading ? (
              <NotificationDetail
                n={reading}
                archived={archived}
                onArchive={(id) => { archive.mutate(id); setReading(null); }}
                onRestore={(id) => { restore.mutate(id); setReading(null); }}
                onMarkUnread={(id) => { markUnread.mutate(id); setReading(null); }}
                onOpen={(route) => navigate(route)}
              />
            ) : (
              <div className="flex h-full items-center justify-center p-8">
                <EmptyState icon={Bell} title="Select a notification" description="Choose a notification from the list to read it here." />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters drawer (<1280px) — the nav rail in a Sheet */}
      <Sheet open={!isXl && railOpen} onOpenChange={setRailOpen}>
        <SheetContent side="left" className="w-72 overflow-y-auto p-4">
          <SheetTitle className="mb-3 text-sm">Filters</SheetTitle>
          <NavRail {...railProps} />
        </SheetContent>
      </Sheet>

      {/* Detail Sheet (<1024px) */}
      <Sheet open={!isDesktop && !!reading} onOpenChange={(o) => !o && setReading(null)}>
        <SheetContent className="w-full max-w-md p-0 sm:max-w-lg">
          {reading && (
            <>
              <SheetTitle className="sr-only">{reading.title}</SheetTitle>
              <NotificationDetail
                n={reading}
                archived={archived}
                onArchive={(id) => { archive.mutate(id); setReading(null); }}
                onRestore={(id) => { restore.mutate(id); setReading(null); }}
                onMarkUnread={(id) => { markUnread.mutate(id); setReading(null); }}
                onOpen={(route) => { setReading(null); navigate(route); }}
              />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
